import { Button, Modal } from 'antd'
import React from 'react'
import { WEB_ENDPOINT_TYPE } from '../../dux/constants/endpoints'
import { NEWS_RESOURCE_FOLDER } from '../../dux/constants/folder'
import { isConsumedId } from '../../dux/modules/sharedEndpoints'
import {
  buildPath,
  getEndpointOfNode,
  validatePrivilege,
} from '../../dux/modules/utils'
import TreeNode from '../sidebar/TreeNode'
import '../styles/Modal.css'

/*
 * Count the subfolders and assets contained within a folder tree
 */
const countFolderContents = (folderId, allFolders, allAssets) => {
  const counts = { folders: 0, assets: 0 }
  const queue = [folderId]
  while (queue.length) {
    const folder = allFolders[queue.shift()]
    if (!folder || folder.deleted) continue
    if (folder.id !== folderId) counts.folders++
    counts.assets += (folder.assets || []).filter(
      (assetId) => allAssets[assetId] && !allAssets[assetId].deleted,
    ).length
    const childFolders = folder.childFolders || []
    childFolders.forEach((childId) => queue.push(childId))
  }
  return counts
}

class PublishFolderModal extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      activeFolder: null,
      openFolderKeys: this.props.appState.openFolderKeys,
      errors: [],
      publishAndApprove: false,
      publish: false,
    }
  }

  allowPublish = (folderId) => {
    return validatePrivilege(
      'publishasset',
      this.props.permissions.privileges,
      this.props.user.email,
      this.props.permissions.groups,
      folderId,
      this.props.folders.byId,
    )
  }

  allowPublishAndApprove = (folderId) => {
    const approve = validatePrivilege(
      'approveasset',
      this.props.permissions.privileges,
      this.props.user.email,
      this.props.permissions.groups,
      folderId,
      this.props.folders.byId,
    )

    return this.allowPublish(folderId) && approve
  }

  validate = () => {
    let errors = []

    // Generate error msg if endpoint folder not selected (or no folder selected)
    if (!this.state.activeFolder)
      errors.push(
        'You must select a folder within an endpoint that allows publishing to complete this ' +
          'request',
      )
    else {
      const ep = getEndpointOfNode(
        this.props.folders.byId[this.state.activeFolder],
        this.props.folders.byId,
        this.props.endpoints.endpoints,
      )
      if (!ep.allowPublishing || ep.type === WEB_ENDPOINT_TYPE)
        errors.push(
          'You must select a folder within an endpoint that allows publishing to complete this ' +
            'request',
        )
    }

    if (errors.length) {
      this.setState({ errors })
      return false
    }

    return true
  }

  save = () => {
    if (this.validate()) {
      this.props.endpointActions.publishFolder(
        this.props.modalProps.folder,
        this.state.activeFolder,
      )
    }
  }

  saveAndApprove = () => {
    if (this.validate()) {
      this.props.endpointActions.publishAndApproveFolder(
        this.props.modalProps.folder,
        this.state.activeFolder,
      )
    }
  }

  nodeIsActive = (id) => this.state.activeFolder === id

  nodeIsDraggable = () => {
    return false
  }
  nodeIsOpen = (id) => this.state.openFolderKeys.indexOf(id) > -1

  onNodeClick = (id) => {
    this.setActiveFolder(id)
  }

  toggleFolder = (id) => {
    if (this.state.openFolderKeys.indexOf(id) < 0) {
      this.setState((prevState) => ({
        openFolderKeys: prevState.openFolderKeys.concat([id]),
      }))
    } else {
      this.setState((prevState) => ({
        openFolderKeys: prevState.openFolderKeys.filter((key) => key !== id),
      }))
    }
  }

  removeOpenFolderKey = (id) => {
    this.setState((prevState) => ({
      openFolderKeys: prevState.openFolderKeys.filter((key) => key !== id),
    }))
  }

  nodeLink = () => this.props.location.pathname

  nodeIsDisabled = (id) => {
    const { user, folders, endpoints, permissions } = this.props

    // Content shared from another BAM! instance is read-only here: a locked
    // endpoint's tree sits in the Published panel, but publishing into it is
    // refused by the server (assertNoConsumedMutation).
    if (isConsumedId(id)) return true

    // Folders may contain any asset type, so the NEWS resource folder and
    // web endpoints are not valid folder publishing destinations.
    if (buildPath(id, folders.byId).indexOf(NEWS_RESOURCE_FOLDER) > -1) {
      return true
    }

    if (
      getEndpointOfNode(folders.byId[id], folders.byId, endpoints.endpoints)
        .type === WEB_ENDPOINT_TYPE
    ) {
      return true
    }

    return !validatePrivilege(
      'publishasset',
      permissions.privileges,
      user.email,
      permissions.groups,
      id,
      folders.byId,
    )
  }

  setActiveFolder = (id) => {
    if (id !== this.state.activeFolder)
      this.setState({
        activeFolder: id,
        publishAndApprove: this.allowPublishAndApprove(id),
        publish: this.allowPublish(id),
        errors: [],
      })
  }

  // Not needed for publish folder modal
  allowPublishing = () => false

  // Not needed for PublishFolderModal
  getBadgeCount = () => 0

  render() {
    const folder = this.props.folders.byId[this.props.modalProps.folder]
    const contents = countFolderContents(
      this.props.modalProps.folder,
      this.props.folders.byId,
      this.props.assets.byId,
    )

    return (
      <Modal
        title="Publish Folder"
        okText="PUBLISH"
        onOk={this.save}
        cancelText="Cancel"
        footer={[
          <Button key="cancel" onClick={this.props.modalActions.closeModal}>
            Cancel
          </Button>,
          // Single PUBLISH button: approves immediately when the user holds
          // approval rights on the selected folder, otherwise submits an
          // approval request (the note in the body explains which will happen)
          <Button
            key="publish"
            type="primary"
            onClick={
              this.state.publishAndApprove ? this.saveAndApprove : this.save
            }
            disabled={!this.state.publish}
            loading={this.props.appState.asyncPending}
          >
            PUBLISH
          </Button>,
        ]}
        onCancel={this.props.modalActions.closeModal}
        open={this.props.modal.modalOpen}
        confirmLoading={this.props.appState.asyncPending}
        maskClosable={false}
      >
        <div style={{ padding: '5px 0' }}>
          Publishing {folder ? folder.name : ''}, including {contents.folders}{' '}
          subfolders and {contents.assets} assets:
        </div>
        <div className="modal__text">Select a destination:</div>
        <div className="modal-tree-container">
          {this.props.endpoints.endpoints
            .filter((endpoint) => endpoint.id !== 'LIB')
            .map((endpoint) => {
              return endpoint.rootFolders.map((root) => {
                return (
                  <TreeNode
                    key={root}
                    {...this.props}
                    activeFolder={this.state.activeFolder}
                    openFolderKeys={this.state.openFolderKeys}
                    removeOpenFolderKey={this.removeOpenFolderKey}
                    node={this.props.folders.byId[root]}
                    nodeLink={this.nodeLink}
                    isFolder
                    isModal={true}
                    nodeIsActive={this.nodeIsActive}
                    nodeIsOpen={this.nodeIsOpen}
                    nodeIsDraggable={this.nodeIsDraggable}
                    rightClickDisabled={true}
                    onToggle={this.toggleFolder}
                    onNodeClick={this.onNodeClick}
                    nodeIsDisabled={this.nodeIsDisabled}
                    endpointId={endpoint.id}
                    setActiveFolder={this.setActiveFolder}
                    allowPublishing={this.allowPublishing}
                    badges={[]}
                    getBadgeCount={this.getBadgeCount}
                  />
                )
              })
            })}
        </div>
        <div>
          {this.state.errors.map((error, index) => (
            <div key={index} className="library__publish-error">
              {error}
            </div>
          ))}
        </div>
        {this.state.activeFolder &&
          this.state.publish &&
          !this.state.publishAndApprove && (
            <div style={{ paddingTop: 10, color: 'rgba(0, 0, 0, 0.65)' }}>
              You don&apos;t have approval rights on this folder — publishing
              will send an approval request to its reviewers.
            </div>
          )}
      </Modal>
    )
  }
}

export default PublishFolderModal
